import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';

import {
  controlledFileDownloadResponseSchema,
  controlledFilesResponseSchema,
  type ControlledFileDownload,
  type ControlledFiles,
} from './types';

// Documentos controlados de un documento (spec 2026-10-20 §6.9): la lista por versión y
// la URL firmada de descarga (5 minutos, cada descarga queda en la bitácora). La empresa
// activa entra al queryKey: nunca se sirve la caché de otra empresa.

const DOCS = '/api/v1/quality/documents';

interface Scope {
  companyId: string | null;
  enabled: boolean;
}

export function useControlledFiles(
  entryId: string,
  { companyId, enabled }: Scope,
): UseQueryResult<ControlledFiles> {
  const api = useApi();
  return useQuery({
    queryKey: ['controlled-files', companyId, entryId],
    queryFn: async () =>
      controlledFilesResponseSchema.parse(
        await api.get<unknown>(`${DOCS}/${encodeURIComponent(entryId)}/restricted-files`),
      ).data,
    // Reintentos: la política global (query-client) no repite un 4xx ni un contrato inválido.
    enabled,
  });
}

export interface DownloadVars {
  entryId: string;
  fileId: string;
}

export function useControlledFileDownload(): UseMutationResult<
  ControlledFileDownload,
  Error,
  DownloadVars
> {
  const api = useApi();
  return useMutation({
    mutationFn: async ({ entryId, fileId }) =>
      controlledFileDownloadResponseSchema.parse(
        await api.get<unknown>(
          `${DOCS}/${encodeURIComponent(entryId)}/restricted-files/${encodeURIComponent(fileId)}/download`,
        ),
      ).data,
  });
}
